// `toFixed` remains for the employee-count percentages below, which are not money.
import { type Money } from "@performyard/money";
import { toFixed } from "@performyard/utils-isomorphic";

import { getColor } from "../../../../utils/getColor";
import type { ManagerBudgetGroupWithCycleAllotment } from "../SharedComponents/utils";
import { budgetGroupKey } from "../SharedComponents/utils";

import { resolveGroupBudget, sumAmounts } from "../SharedComponents/money-aggregation";

export type CycleBudgetOverviewTableRow = {
	_id: string;
	managerId: string;
	actingManagerId: string | null;
	managerName: string;
	actingManagerName: string | null;
	managerDirectReportCount: number;
	budget: Money;
	status: string;
};

const statusMap = {
	in_review: "Submitted",
	pending: "Not Submitted",
	approved: "Approved",
};

export const enhanceManagerGroupData = (
	managerGroups: ManagerBudgetGroupWithCycleAllotment[]
): CycleBudgetOverviewTableRow[] => {
	return managerGroups
		.map((group) => ({
			_id: budgetGroupKey(group),
			managerId: group.managerId,
			actingManagerId: group.overrideManagerId,
			managerName: group.managerName,
			actingManagerName: group.overrideManagerName,
			managerDirectReportCount: group.managerDirectReportCount,
			// The budget allocated and snapshotted at creation; older cycles without
			// one fall back to the cycle percent of the group's salary total.
			budget: resolveGroupBudget(
				group.groupIncreaseBudget,
				sumAmounts(group.results.map((r) => r.currentSalary)),
				group.allotmentIncreasePercent
			),
			status: statusMap[group.status],
		}))
		.sort((a, b) => a.managerName.localeCompare(b.managerName));
};

export const buildBucketsForAvgIncreaseWidget = (sortedValueData: number[]) => {
	const bucketConfig = [
		{ label: "0-2", min: 0, max: 2, color: getColor("primary", 2) },
		{ label: "2-4", min: 2, max: 4, color: getColor("primary", 3) },
		{ label: "4-6", min: 4, max: 6, color: getColor("primary", 3) },
		{ label: "6-8", min: 6, max: 8, color: getColor("primary", 5) },
		{ label: "8+", min: 8, max: Infinity, color: getColor("primary", 6) },
	] as const;

	const total = sortedValueData.length;

	const buckets = Object.fromEntries(
		bucketConfig.map(({ label, min, max, color }) => {
			const count = sortedValueData.filter((v) => v >= min && v < max).length;
			return [
				label,
				{
					value: total > 0 ? toFixed((count / total) * 100, 2) : 0,
					color,
					unit: "employee",
					valueCount: count,
				},
			];
		})
	);

	return buckets;
};
